'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { strings } from '@/content/strings';

const ANALYTICS_COOKIE = 'analytics_consent';
const MARKETING_COOKIE = 'marketing_consent';
const OPT_OUT_COOKIE = 'ccpa_opt_out';

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [ready, setReady] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [analyticsAllowed, setAnalyticsAllowed] = useState(true);
  const [marketingAllowed, setMarketingAllowed] = useState(true);

  useEffect(() => {
    const analyticsConsent = readCookie(ANALYTICS_COOKIE);
    const marketingConsent = readCookie(MARKETING_COOKIE);
    const onReady = () => setReady(true);
    if (document.readyState === 'complete') {
      onReady();
    } else {
      window.addEventListener('load', onReady, { once: true });
    }
    if (!analyticsConsent) {
      setVisible(true);
    }
    setAnalyticsAllowed(analyticsConsent !== 'declined');
    setMarketingAllowed(marketingConsent !== 'declined');
    return () => window.removeEventListener('load', onReady);
  }, []);

  if (!visible || !ready) return null;

  const savePreferences = () => {
    setCookie(ANALYTICS_COOKIE, analyticsAllowed ? 'accepted' : 'declined', 365);
    setCookie(MARKETING_COOKIE, marketingAllowed ? 'accepted' : 'declined', 365);
    if (!marketingAllowed) {
      setCookie(OPT_OUT_COOKIE, '1', 365);
    } else {
      deleteCookie(OPT_OUT_COOKIE);
    }
    setVisible(false);
    setShowPreferences(false);
  };

  const acceptAll = () => {
    setCookie(ANALYTICS_COOKIE, 'accepted', 365);
    setCookie(MARKETING_COOKIE, 'accepted', 365);
    deleteCookie(OPT_OUT_COOKIE);
    setVisible(false);
    setShowPreferences(false);
  };

  return (
    <div className="fixed inset-x-0 bottom-4 z-50 px-4">
      <div className="mx-auto max-w-3xl rounded-2xl border border-border bg-card shadow-xl p-4 text-card-foreground">
        <div className="space-y-3">
          <div className="text-sm font-semibold text-foreground">
            {strings.common.cookies.title}
          </div>
          <p className="text-xs text-muted-foreground">
            {strings.common.cookies.intro}
          </p>
          <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
            <Link href="/privacy" className="text-primary hover:opacity-80">
              {strings.common.cookies.privacyPolicy}
            </Link>
            <Link href="/do-not-sell" className="text-primary hover:opacity-80">
              {strings.common.cookies.doNotSell}
            </Link>
            <Link href="/notice-at-collection" className="text-primary underline underline-offset-4 hover:opacity-80">
              {strings.common.cookies.noticeAtCollection}
            </Link>
          </div>
          {showPreferences && (
            <div className="rounded-lg border border-border bg-ghost px-3 py-3 text-xs text-foreground space-y-2">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-foreground">{strings.common.cookies.essential}</div>
                  <div className="text-muted-foreground">{strings.common.cookies.essentialHelper}</div>
                </div>
                <div className="text-[11px] text-muted-foreground">{strings.common.cookies.alwaysOn}</div>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-foreground">{strings.common.cookies.analytics}</div>
                  <div className="text-muted-foreground">{strings.common.cookies.analyticsHelper}</div>
                </div>
                <input
                  type="checkbox"
                  checked={analyticsAllowed}
                  onChange={(event) => setAnalyticsAllowed(event.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-foreground">{strings.common.cookies.marketing}</div>
                  <div className="text-muted-foreground">{strings.common.cookies.marketingHelper}</div>
                </div>
                <input
                  type="checkbox"
                  checked={marketingAllowed}
                  onChange={(event) => setMarketingAllowed(event.target.checked)}
                  className="h-4 w-4 rounded border-border text-primary focus:ring-primary"
                />
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={acceptAll}
              className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
            >
              {strings.common.cookies.acceptAll}
            </button>
            <button
              type="button"
              onClick={() => setShowPreferences((prev) => !prev)}
              className="rounded-lg border border-border bg-ghost px-4 py-2 text-xs font-semibold text-foreground hover:bg-primary/10"
            >
              {showPreferences ? strings.common.cookies.hidePreferences : strings.common.cookies.managePreferences}
            </button>
            {showPreferences && (
              <button
                type="button"
                onClick={savePreferences}
                className="rounded-lg bg-primary px-4 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90"
              >
                {strings.common.cookies.savePreferences}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function readCookie(name: string) {
  if (typeof document === 'undefined') return null;
  const match = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith(`${name}=`));
  return match ? match.split('=')[1] : null;
}

function setCookie(name: string, value: string, days: number) {
  if (typeof document === 'undefined') return;
  const maxAge = days * 24 * 60 * 60;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${value}; Max-Age=${maxAge}; Path=/; SameSite=Lax${secure}`;
}

function deleteCookie(name: string) {
  if (typeof document === 'undefined') return;
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${secure}`;
}
